import React from "react";
import clsx from "clsx";
import { useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "@SonarCX/hooks/redux";
import { Edit2, Hierarchy, Paperclip2 } from "iconsax-react";
import { useTheme } from "@mui/material";

import Button from "@SonarCX/components/atoms/Button";
import Content from "./Content";
import FavoriteButton from "@SonarCX/components/atoms/FavoriteButton";
import Comments from "./Comments";
import Description from "./Description";

import { Routes } from "@SonarCX/Routes";
import { AddErrorAlert, AddSuccessAlert } from "@SonarCX/slices/alert";
import {
  selectAllTasksTable,
  toggleSelected,
} from "@SonarCX/slices/allTasksTable";
import { useTaskAPI } from "@SonarCX/providers/TaskAPIProvider";
import { useTaskComments } from "@SonarCX/providers/TaskCommentsProvider";
import { useTaskFilters } from "@SonarCX/providers/TaskFiltersProvider";
import {
  useTaskPageState,
  ALL_TAB,
} from "@SonarCX/providers/TaskPageStateProvider";
import useCombinedTableState from "@SonarCX/hooks/useCombinedTableState";
import useTaskPermissions from "@SonarCX/hooks/permissions/useTaskPermissions";
import useTaskInputOpen from "@SonarCX/hooks/useTaskInputOpen";
import useFavorites from "@SonarCX/hooks/useFavorites";
import useStyles from "./styles";

const DELAY = 100;

const Title = ({ task, lighter, mobile = false, onMove }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { selected } = useAppSelector(selectAllTasksTable);

  const { favoriteTasks } = useFavorites();
  const { updateTask } = useTaskAPI();
  const {
    counts,
    viewingTaskCommentsID,
    setViewingTaskCommentsID,
    shouldCheckClose,
    setShouldCheckClose,
  } = useTaskComments();
  const { includeSubtasks } = useTaskFilters();
  const { tab } = useTaskPageState();
  const { tasks } = useCombinedTableState();
  const { canEdit } = useTaskPermissions(task);
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(task, "TITLE");

  const [hovering, setHovering] = React.useState(false);
  const [startingValue, setStartingValue] = React.useState(task.title);
  const [value, setValue] = React.useState(startingValue);
  const [shouldMove, setShouldMove] = React.useState(false);

  const inputRef = React.useRef();
  const count = React.useMemo(() => task.sub_tasks ?? 0, [task.sub_tasks]);
  const comments = React.useMemo(() => {
    return Math.max(counts[task.id] ?? 0, task?.comments ?? 0);
  }, [counts, task.id, task.comments]);
  const isSelected = React.useMemo(() => {
    return selected.includes(task.id);
  }, [selected, task.id]);
  const [hoveringRow, setHoveringRow] = React.useState(false);

  const classes = useStyles({
    lighter,
    canEdit,
    mobile,
    closed: Boolean(task.closed_at),
  });

  const showCommentPreview = React.useMemo(() => {
    return viewingTaskCommentsID === task.id;
  }, [viewingTaskCommentsID, task.id]);

  const setShowCommentPreview = React.useCallback(
    (show) => {
      if (show) {
        setViewingTaskCommentsID(task.id);
      } else {
        setViewingTaskCommentsID(null);
      }
    },
    [task.id, setViewingTaskCommentsID],
  );

  const hideSubs = React.useMemo(
    () => tab === ALL_TAB && includeSubtasks,
    [tab, includeSubtasks],
  );
  const showParentTitle = React.useMemo(
    () => hideSubs && Boolean(task.parent?.title),
    [hideSubs, task],
  );

  const mediaCount = React.useMemo(() => {
    if (!task?.media_ids) {
      return 0;
    }

    return task.media_ids.length;
  }, [task]);

  const showDetails = React.useMemo(() => {
    return count > 0 || comments > 0 || (mediaCount > 0 && !task.parent_id);
  }, [count, comments, mediaCount, task]);

  const isFavorite = React.useMemo(() => {
    if (!task.id) return false;

    return favoriteTasks.some((fav) => fav.item_id === task.id);
  }, [favoriteTasks, task]);

  const halt = React.useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleBlur = React.useCallback(
    async (e) => {
      try {
        halt(e);

        setTimeout(() => {
          handleClose();
        }, DELAY);

        if (startingValue === value) return;

        const newTask = { ...task, title: value };

        await updateTask(newTask.id, newTask);

        dispatch(AddSuccessAlert("Changes saved"));
      } catch (err) {
        setValue(startingValue);
        dispatch(AddErrorAlert({ err }));
      }
    },
    [halt, dispatch, handleClose, startingValue, value, task, updateTask],
  );
  const onTaskClick = React.useCallback(() => {
    if (isOpen && !mobile) {
      handleClose();
    } else {
      setShouldMove(true);
    }
  }, [isOpen, handleClose, mobile]);

  // Moves away only once
  React.useEffect(() => {
    if (shouldMove) {
      onMove(task.id, tasks);
      if (mobile) {
        history.push(`${Routes.TASKS}/${task.id}`);
      }
    }
  }, [shouldMove, onMove, task.id, history, tasks, mobile]);

  React.useEffect(() => {
    setStartingValue(task.title);
    setValue(task.title);
  }, [task.title]);

  // reset because of timeout conflicts
  React.useEffect(() => {
    if (!showCommentPreview && shouldCheckClose) {
      setShouldCheckClose(false);
    }
  }, [showCommentPreview, shouldCheckClose, setShouldCheckClose]);

  return (
    <div
      onMouseEnter={() => setHoveringRow(true)}
      onMouseLeave={() => setHoveringRow(false)}
      className={classes.parentRoot}
    >
      {!mobile && (
        <div className={classes.checkboxContainer}>
          {(hoveringRow || isSelected) && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => dispatch(toggleSelected(task.id))}
              className={classes.checkbox}
            />
          )}
        </div>
      )}

      <div
        onClick={onTaskClick}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        style={isOpen ? { flex: "1" } : {}}
        className={classes.root}
      >
        {isOpen && canEdit ? (
          <div onClick={halt} className={classes.input}>
            <input
              ref={inputRef}
              placeholder="Title"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={(e) => e.key === "Enter" && handleBlur(e)}
              autoFocus
            />

            <Button onClick={handleClose} className="button">
              SAVE
            </Button>
          </div>
        ) : (
          <div
            onClick={onTaskClick}
            className={clsx(
              classes.title,
              lighter && classes.titleLight,
              !lighter && classes.titleBold,
            )}
          >
            <Content
              task={task}
              value={value}
              halt={halt}
              showParentTitle={showParentTitle}
              classes={classes}
            />

            <Description task={task} />

            {hovering && !mobile ? (
              <>
                <Edit2
                  size={theme.size.sm}
                  variant="Outline"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(e);
                  }}
                  className={classes.edit}
                />
                <FavoriteButton
                  isFavorite={isFavorite}
                  type="task"
                  itemId={task.id}
                />
              </>
            ) : isFavorite ? (
              <FavoriteButton
                isFavorite={isFavorite}
                type="task"
                itemId={task.id}
              />
            ) : (
              <div style={{ width: "1.5rem" }} />
            )}
          </div>
        )}
      </div>

      {showDetails && (
        <div className={classes.details}>
          {count > 0 && (
            <div className={classes.count}>
              <span className="count">{count}</span>

              <span className="icon">
                <Hierarchy
                  size={theme.size.sm}
                  variant="Outline"
                  className={classes.icon}
                />
              </span>
            </div>
          )}

          <Comments
            task={task}
            showCommentPreview={showCommentPreview}
            setShowCommentPreview={setShowCommentPreview}
            shouldCheckClose={shouldCheckClose}
            setShouldCheckClose={setShouldCheckClose}
          />

          {/* Only show for parent tasks */}
          {mediaCount > 0 && !task.parent_id && (
            <div className={classes.count} onClick={halt}>
              <span className="count">{mediaCount}</span>

              <span className="icon">
                <Paperclip2 size={theme.size.sm} variant="Outline" />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(Title);
